import { useCallback, useEffect, useState, useRef } from 'react';
import { getGCPData } from 'services/resources/slides';
import { DELETE_SLIDE_COMMENT_SUCCESS_MESSAGE } from 'constants/successMessages';
import {
  ADD_SLIDE_COMMENT_ERROR_MESSAGE,
  DELETE_SLIDE_COMMENT_ERROR_MESSAGE,
  FETCHING_ANNOTATIONS_ERROR_MESSAGE,
  FETCHING_SLIDES_ERROR_MESSAGE,
} from 'constants/errorMessages';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { KEYS, KEYS_CODES } from 'constants/keyboard';
import { useSelector } from 'react-redux';
import { openSlideInFullscreenSelector } from 'store/slices/slidesListSlice/slidesListSlice';
import {
  ANNOTATION_COLOR_FIELD,
  ANNOTATION_DRAW_MODE_ENABLED_FIELD,
  ANNOTATION_DRAW_TOOL_FIELD,
  ANNOTATION_STROKE_WIDTH_FIELD,
  DISTANCE_MEASURE_ENABLED_FIELD,
  ROTATE_DIRECTIONS,
} from 'components/ImageViewer/constants';
import { useUpdateUserPreferencesMutation } from 'store/apis/userDetailsApi';
import { currentUserPreferencesSelector } from 'store/slices/userDetailsSlice';
import { useClickHold } from 'components/utilities/hooks/mouse/useClickHold';
import { useLongPressClick } from 'components/utilities/hooks/mouse/useLongPressClick';
import {
  useCreateSlideCommentMutation,
  useDeleteSlideCommentMutation,
  useLazyGetSharedSlidesCommentsQuery,
  useLazyGetSlideCommentsQuery,
} from 'store/apis/slideCommentsApi';
import OpenSeadragon from 'openseadragon';

export const useMultiSlidesComments = (slides, isSharedPage) => {
  const { showError, showSuccess } = useSnackbar();
  // Here we use Map instead of Object because slide object is used as key
  const [slidesCommentsGroups, setSlidesCommentsGroups] = useState(new Map());
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);

  const [getSlideComments] = useLazyGetSlideCommentsQuery();
  const [getSharedSlidesComments] = useLazyGetSharedSlidesCommentsQuery();
  const [createSlideCommentMutation] = useCreateSlideCommentMutation();
  const [deleteSlideComment] = useDeleteSlideCommentMutation();

  useEffect(() => {
    if (!slides) return;

    const slidesForWhichCommentsShouldBeFetched = slides.filter(
      (slide) => !slidesCommentsGroups.get(slide),
    );

    const slidesCommentsPromises = slidesForWhichCommentsShouldBeFetched.map(
      (slide) =>
        isSharedPage
          ? getSharedSlidesComments({ shareUUID: slide.share_uuid }).unwrap()
          : getSlideComments({ slideUUID: slide.uuid }).unwrap(),
    );

    if (slidesCommentsPromises.length) {
      setIsCommentsLoading(true);

      Promise.all(slidesCommentsPromises)
        .then((slideCommentsResponse) => {
          setSlidesCommentsGroups((prevGroups) => {
            slideCommentsResponse.forEach((comments, index) => {
              const slide = slidesForWhichCommentsShouldBeFetched[index];

              prevGroups.set(slide, {
                isOpened: false,
                uuid: slide.uuid,
                comments,
              });
            });

            return new Map(prevGroups);
          });
        })
        .catch(() => {
          showError(FETCHING_SLIDES_ERROR_MESSAGE);
        })
        .finally(() => setIsCommentsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides]);

  const handleDeleteComment = useCallback(
    (commentToDelete, slide) => {
      deleteSlideComment({
        slideUUID: commentToDelete.slide.uuid,
        commentUUID: commentToDelete.uuid,
      })
        .unwrap()
        .then(() => {
          setSlidesCommentsGroups((prevGroups) => {
            const group = prevGroups.get(slide);
            const updatedCommentsList = group.comments.filter(
              (comment) => comment.uuid !== commentToDelete.uuid,
            );

            prevGroups.set(slide, {
              ...group,
              comments: [...updatedCommentsList],
            });

            return new Map(prevGroups);
          });

          showSuccess(DELETE_SLIDE_COMMENT_SUCCESS_MESSAGE);
        })
        .catch(() => showError(DELETE_SLIDE_COMMENT_ERROR_MESSAGE));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSlidesCommentsGroups],
  );

  const handleCreateComment = useCallback(
    (newSlideCommentText, slide, isInternal) => {
      createSlideCommentMutation({
        slideUUID: slide.uuid,
        text: newSlideCommentText,
        isInternal,
      })
        .unwrap()
        .then((response) => {
          if (response) {
            setSlidesCommentsGroups((prevGroups) => {
              const group = prevGroups.get(slide);

              prevGroups.set(slide, {
                ...group,
                comments: [response, ...group.comments],
              });

              return new Map(prevGroups);
            });
          }
        })
        .catch(() => showError(ADD_SLIDE_COMMENT_ERROR_MESSAGE));
    },
    [setSlidesCommentsGroups],
  );

  return {
    slidesCommentsGroups,
    isCommentsLoading,
    handleCreateComment,
    handleDeleteComment,
  };
};

const useRotateCallback = (
  id,
  direction,
  viewer,
  specificIncrement = undefined,
) =>
  useCallback(() => {
    const { viewport, rotationIncrement, container } = viewer;
    const increment = specificIncrement || rotationIncrement;

    if (viewport && container) {
      let rotation = viewport.getRotation();

      const defaultRotation = OpenSeadragon.positiveModulo(
        rotation + increment,
        360,
      );

      const reversedRotation = OpenSeadragon.positiveModulo(
        rotation - increment,
        360,
      );

      if (!viewport.flipped) {
        rotation =
          direction === ROTATE_DIRECTIONS.LEFT
            ? reversedRotation
            : defaultRotation;
      } else {
        rotation =
          direction === ROTATE_DIRECTIONS.LEFT
            ? defaultRotation
            : reversedRotation;
      }

      viewport.setRotation(rotation);
    }
  }, [direction, viewer, specificIncrement]);

export const useSideRotation = (viewerState, direction, id) => {
  const rotateCallbackHold = useRotateCallback(id, direction, viewerState);

  const rotateCallbackClick90 = useRotateCallback(
    id,
    direction,
    viewerState,
    90,
  );

  const rotateCallbackHoldClick = useClickHold(rotateCallbackHold);

  return useLongPressClick(rotateCallbackClick90, rotateCallbackHoldClick);
};

export const useKeyboardShortcuts = (
  viewerState,
  toggleFullScreen,
  showSlidesNavigation,
  handleScreenshot,
  handlePreviousSlide,
  handleNextSlide,
) => {
  const openInFullPage = useSelector(openSlideInFullscreenSelector);

  const leftRotationActions = useSideRotation(
    viewerState,
    ROTATE_DIRECTIONS.LEFT,
    null,
  );
  const rightRotationActions = useSideRotation(
    viewerState,
    ROTATE_DIRECTIONS.RIGHT,
    null,
  );

  const getViewerPageKeyDownHandlers = (e, viewer) => ({
    [KEYS_CODES.ARROW_LEFT]: () => {
      if (showSlidesNavigation) {
        const isFullScreen = viewerState?.isFullPage();
        if (isFullScreen) {
          toggleFullScreen();
        }
        handlePreviousSlide(isFullScreen);
      }
      e.preventDefault();
      e.stopPropagation();
    },
    [KEYS_CODES.ARROW_RIGHT]: () => {
      if (showSlidesNavigation) {
        const isFullScreen = viewerState?.isFullPage();

        if (isFullScreen) {
          toggleFullScreen();
        }
        handleNextSlide(isFullScreen);
      }
      e.preventDefault();
      e.stopPropagation();
    },
    [KEYS_CODES.ESC]: () => viewer?.setFullPage(false),
  });

  const getCanvasKeyDownHandlers = (e, viewer) => {
    const handlePan = (x, y) => {
      viewer.viewport.panTo(new OpenSeadragon.Point(x, y), true);
      viewer.viewport.applyConstraints();
      e.preventDefault();
      e.stopPropagation();
    };

    const getCenterX = (bounds) =>
      (bounds.getTopLeft().x + bounds.getTopRight().x) / 2;
    const getCenterY = (bounds) =>
      (bounds.getTopLeft().y + bounds.getBottomLeft().y) / 2;

    return {
      [KEYS_CODES.F]: () => {
        toggleFullScreen();
        e.preventDefault();
        e.stopPropagation();
      },
      [KEYS_CODES.P]: () => {
        const canvas = viewerState?.drawer?.canvas;
        const canvasSizes = {
          width: canvas?.width,
          height: canvas?.height,
        };
        handleScreenshot(null, canvasSizes);
      },
      [KEYS_CODES.Q]: () => {
        leftRotationActions.onMouseDown();
        e.preventDefault();
        e.stopPropagation();
      },
      [KEYS_CODES.E]: () => {
        rightRotationActions.onMouseDown();
        e.preventDefault();
        e.stopPropagation();
      },
      [KEYS.CONTROL + KEYS_CODES.ARROW_UP]: () => {
        const bounds = viewer.viewport.getBounds();
        const newY = Math.max(0, bounds.getTopLeft().y + bounds.height * 1.5);
        handlePan(getCenterX(bounds), newY);
      },
      [KEYS.CONTROL + KEYS_CODES.ARROW_DOWN]: () => {
        const bounds = viewer.viewport.getBounds();
        const newY = bounds.getBottomLeft().y - bounds.height * 1.5;
        handlePan(getCenterX(bounds), newY);
      },
      [KEYS.CONTROL + KEYS_CODES.ARROW_LEFT]: () => {
        const bounds = viewer.viewport.getBounds();
        const newX = Math.max(0, bounds.getTopLeft().x + bounds.width * 1.5);
        handlePan(newX, getCenterY(bounds));
      },
      [KEYS.CONTROL + KEYS_CODES.ARROW_RIGHT]: () => {
        const bounds = viewer.viewport.getBounds();
        const newX = Math.min(1, bounds.getTopRight().x - bounds.width * 1.5);
        handlePan(newX, getCenterY(bounds));
      },
    };
  };

  const getCanvasKeyUpHandlers = (e) => ({
    [KEYS_CODES.Q]: () => {
      leftRotationActions.onMouseUp();
      e.preventDefault();
      e.stopPropagation();
    },
    [KEYS_CODES.E]: () => {
      rightRotationActions.onMouseUp();
      e.preventDefault();
      e.stopPropagation();
    },
  });

  const callHandler = (e, handlersGetter, viewer) => {
    const tagsToExclude = ['INPUT', 'TEXTAREA'];
    const { keyCode } = e;
    const isCtrlPressed = e.ctrlKey;

    if (
      KEYS_CODES.hasOwnProperty(keyCode) ||
      openInFullPage ||
      tagsToExclude.includes(e.target.tagName) ||
      e.repeat
    ) {
      return;
    }

    const handlers = handlersGetter(e, viewer);
    if (isCtrlPressed) {
      handlers[KEYS.CONTROL + keyCode]?.(e);
    } else {
      handlers[keyCode]?.(e);
    }
  };

  useEffect(() => {
    const canvasKeyDownListener = (e) =>
      callHandler(e, getCanvasKeyDownHandlers, viewerState);
    const viewerPageKeyDownListener = (e) =>
      callHandler(e, getViewerPageKeyDownHandlers, viewerState);
    const viewerPageKeyUpListener = (e) =>
      callHandler(e, getCanvasKeyUpHandlers, viewerState);

    if (viewerState) {
      viewerState.container?.addEventListener('keydown', canvasKeyDownListener);
      window.addEventListener('keydown', viewerPageKeyDownListener);
      window.addEventListener('keyup', viewerPageKeyUpListener);
    }

    return () => {
      if (viewerState) {
        viewerState.container?.removeEventListener(
          'keydown',
          canvasKeyDownListener,
        );
        window.removeEventListener('keydown', viewerPageKeyDownListener);
        window.removeEventListener('keyup', viewerPageKeyUpListener);
      }
    };
  }, [showSlidesNavigation, viewerState]);
};

export const useExternalAnnotations = (
  displayedAnnotations,
  externalAnnotations = [],
  showError,
) => {
  const [annotationAttachments, setAnnotationAttachments] = useState(null);
  const prevExternalAnnotationsRef = useRef([]);

  useEffect(() => {
    if (
      externalAnnotations.length &&
      displayedAnnotations?.length !== prevExternalAnnotationsRef.current.length
    ) {
      const promises = externalAnnotations
        .filter((anno) => displayedAnnotations.includes(anno.uuid))
        .map((annotation) =>
          getGCPData(annotation.file.url).then((data) => ({
            ...annotation,
            data,
          })),
        );

      Promise.all(promises)
        .then((results) => {
          setAnnotationAttachments(results);
          prevExternalAnnotationsRef.current = displayedAnnotations;
        })
        .catch((error) => {
          showError(error?.message || FETCHING_ANNOTATIONS_ERROR_MESSAGE);
        });
    }
  }, [externalAnnotations, showError, displayedAnnotations]);

  return annotationAttachments;
};

export const VIEWER_MODES = {
  SELECT: 'select',
  DRAW: 'draw',
  MEASURE: 'measure',
};

/**
 * A fast pan must not end in an annotation being selected. Only the select
 * mode owns `disableSelect` here: while a tool is active the mode effect keeps
 * selection off, and a drag-end (measure mode still pans through OSD's drag
 * events) must not switch it back on.
 */
export const usePreventAnnotationSelectOnDrag = ({
  viewerRef,
  annoRef,
  modeRef,
}) => {
  useEffect(() => {
    if (viewerRef.current && annoRef.current) {
      viewerRef.current.addHandler('canvas-drag-end', ({ speed }) => {
        const isSelectMode =
          !modeRef || modeRef.current === VIEWER_MODES.SELECT;

        if (!isSelectMode || !annoRef.current) {
          return;
        }

        if (speed > 1) {
          annoRef.current.disableSelect = true;
        }

        setTimeout(() => {
          if (
            annoRef.current &&
            (!modeRef || modeRef.current === VIEWER_MODES.SELECT)
          ) {
            annoRef.current.disableSelect = false;
          }
        }, 20);
      });
    }
  }, [viewerRef.current]);
};

const getViewerMode = (preferences) => {
  if (preferences[DISTANCE_MEASURE_ENABLED_FIELD]) {
    return VIEWER_MODES.MEASURE;
  }

  if (preferences[ANNOTATION_DRAW_MODE_ENABLED_FIELD]) {
    return VIEWER_MODES.DRAW;
  }

  return VIEWER_MODES.SELECT;
};

/**
 * Single source of truth for the annotation interaction mode.
 *
 * The mode is derived from the existing preferences so no stored user
 * preference has to be migrated; every change is persisted in one update
 * so the draw and measure flags can never contradict each other.
 */
export const useViewerMode = () => {
  const preferences = useSelector(currentUserPreferencesSelector);
  const [updateUserPreferences] = useUpdateUserPreferencesMutation();

  const mode = getViewerMode(preferences);

  const setMode = useCallback(
    (nextMode) => {
      if (nextMode === mode) {
        return;
      }

      updateUserPreferences({
        postParams: {
          [DISTANCE_MEASURE_ENABLED_FIELD]: nextMode === VIEWER_MODES.MEASURE,
          [ANNOTATION_DRAW_MODE_ENABLED_FIELD]: nextMode === VIEWER_MODES.DRAW,
        },
        partial: true,
      });
    },
    [mode, updateUserPreferences],
  );

  const setDrawTool = useCallback(
    (tool) =>
      updateUserPreferences({
        postParams: {
          [ANNOTATION_DRAW_TOOL_FIELD]: tool,
        },
        partial: true,
      }),
    [updateUserPreferences],
  );

  const setDrawColor = useCallback(
    (color) =>
      updateUserPreferences({
        postParams: {
          [ANNOTATION_COLOR_FIELD]: color,
        },
        partial: true,
      }),
    [updateUserPreferences],
  );

  const setDrawStrokeWidth = useCallback(
    (strokeWidth) =>
      updateUserPreferences({
        postParams: {
          [ANNOTATION_STROKE_WIDTH_FIELD]: strokeWidth,
        },
        partial: true,
      }),
    [updateUserPreferences],
  );

  return {
    mode,
    setMode,
    setDrawTool,
    setDrawColor,
    setDrawStrokeWidth,
  };
};

// Annotorious' comment widget textarea; it is focused as soon as an
// annotation is selected.
export const ANNOTATION_COMMENT_FIELD_CLASS = 'r6o-editable-text';

const isTextField = (target) => {
  const tag = target?.tagName;

  return tag === 'INPUT' || tag === 'TEXTAREA';
};

/**
 * Selecting an annotation opens the comment editor with its (empty) textarea
 * focused. Until something has been typed there, Delete/Backspace still mean
 * "delete the shape", not "edit the comment".
 */
const isEmptyAnnotationCommentField = (target) =>
  !!target?.classList?.contains(ANNOTATION_COMMENT_FIELD_CLASS) &&
  !target.value;

/**
 * Esc ladder: exit the annotation mode first, then clear the selection.
 * Delete/Backspace removes the selected annotation.
 * Ignored while the user is typing (comment widgets, inputs), except for
 * Delete/Backspace in the untouched comment field of the selected annotation.
 */
export const useViewerModeKeyboardShortcuts = ({
  mode,
  setMode,
  annoRef,
  onDeleteSelected,
}) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      const { target } = event;
      const isDeleteKey = event.key === 'Delete' || event.key === 'Backspace';

      if (
        isTextField(target) &&
        !(isDeleteKey && isEmptyAnnotationCommentField(target))
      ) {
        return;
      }

      if (event.key === 'Escape') {
        if (mode !== VIEWER_MODES.SELECT) {
          setMode(VIEWER_MODES.SELECT);

          return;
        }

        annoRef.current?.cancelSelected();

        return;
      }

      if (isDeleteKey && mode === VIEWER_MODES.SELECT) {
        const selectedAnnotation = annoRef.current?.getSelected?.();

        if (selectedAnnotation) {
          event.preventDefault();
          onDeleteSelected?.(selectedAnnotation);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, setMode, annoRef, onDeleteSelected]);
};
